// theme/scripts/components/cuffs.js

import axios from 'axios';
import React, { useState, useEffect, useContext, useRef } from 'react';
import SelectComponent from '../common/select';
import OptionComponent from '../common/option';
import { CustomizeContext } from '../contexts/customize';

const client = axios.create({
  baseURL: process.env.REACT_APP_API_URL + '/get_cuffs'
});

const productId = document.getElementById('mtm-customize')?.dataset?.productId ?? '';

const MtmCuffs = () => {
  const [list, setList] = useState([]);
  const [error, setError] = useState(null);
  const { customizes, setCustomizes, setCustomizesForce } = useContext(CustomizeContext);

  // 🔸 reorderがすでに適用されたかどうか
  const reorderApplied = useRef(false);
  const prevCollarValue = useRef(customizes.collar?.value);

  // 🧩 collarが変更されたら、cuffsリセット＋エラー表示
  useEffect(() => {
    const currentCollarValue = customizes.collar?.value;
    if (!prevCollarValue.current) {
      prevCollarValue.current = currentCollarValue;
      return;
    }

    if (prevCollarValue.current !== currentCollarValue) {
      // cuffsがすでに選択されている場合のみリセット＋警告
      if (customizes.cuffs?.value) {
        console.warn('⚠️ Collar changed → resetting cuffs');
        setError('⚠️ Collar was changed. Please reselect your cuffs.');
        setCustomizesForce((prev) => ({
          ...prev,
          cuffs: {
            id: '',
            label: '',
            value: '',
            img: '',
            cuffs_type: {
              id: '',
              label: '',
              value: '',
            },
          },
        }));
      }

      // collarを変更した場合は reorder 再適用を無効化
      reorderApplied.current = true;
    }

    prevCollarValue.current = currentCollarValue;
  }, [customizes.collar?.value]);

  useEffect(() => {
    if (customizes.cuffs?.value) {
      setError(null);
    }
  }, [customizes.cuffs?.value]);

  // 🧭 cuffsリスト取得 + 初回のみreorder反映
  useEffect(() => {
    let mounted = true;

    const getList = async () => {
      try {
        const response = await client.get(
          `?product_id=${productId}&neck_id=${customizes.collar.id}&neck_value=${customizes.collar.value}`
        );
        if (!mounted) return;

        const cuffsList = response.data.result || [];
        setList(cuffsList);

        // 🚫 reorderの再適用を防止
        if (reorderApplied.current) return;

        const reorderRaw = sessionStorage.getItem('reorder_properties');
        if (!reorderRaw) return;

        reorderApplied.current = true; // ✅ 一度だけ適用

        const reorderData = JSON.parse(reorderRaw);
        const reorderLabel = reorderData._Cuffs?.trim();
        const reorderType = reorderData._Cuffs_Type?.trim();

        const matchedCuffs = cuffsList.find(
          (item) =>
            item.label?.trim().toLowerCase() === reorderLabel?.toLowerCase() ||
            item.value?.toString() === reorderLabel
        );

        if (!matchedCuffs) {
          setError(`⚠️ Reorder: "${reorderLabel}" does not exist in this fabric. Please select again.`);
          return;
        }

        const matchedType =
          matchedCuffs.cuffs_type?.find(
            (t) =>
              t.label?.trim().toLowerCase() === reorderType?.toLowerCase() ||
              t.value?.toString() === reorderType
          ) || null;

        setCustomizes((prev) => ({
          ...prev,
          cuffs: {
            id: matchedCuffs.id,
            label: matchedCuffs.label,
            value: matchedCuffs.value,
            img: matchedCuffs.image_url,
            cuffs_type: matchedType
              ? {
                  id: matchedType.id,
                  label: matchedType.label,
                  value: matchedType.value,
                }
              : null,
          },
        }));

        console.log('✅ reorder cuffs applied once:', reorderLabel, reorderType);
      } catch (err) {
        console.error('❌ cuffs API Error:', err);
        setError('❌ APIエラーによりカフス情報を取得できませんでした。');
      }
    };

    getList();
    return () => {
      mounted = false;
    };
  }, [customizes.collar?.id, customizes.collar?.value]);

  // reorder適用時のUI展開
  useEffect(() => {
    const reorderRaw = sessionStorage.getItem('reorder_properties');
    if (!reorderRaw || !list.length) return;

    const el = document.getElementById('mtm-cuffs');
    if (el) {
      el.classList.add('active');
      el.nextElementSibling?.classList.add('active');
    }
  }, [list, customizes.cuffs?.id]);

  if (!list || list.length === 0) return null;

  return (
    <div
      className="mtm-item__wrapper"
      id="mtm-cuffs"
      data-neck-id={customizes.collar.id}
      data-neck-value={customizes.collar.value}
    >
      <h3 className="mtm-item__title">CUFFS</h3>
      {error && <p style={{ color: 'red' }}>{error}</p>}

      <div className="mtm-item__selects row row-cols-2 row-cols-md-4">
        {list.map((item, ind) => (
          <SelectComponent
            item={item}
            ind={ind}
            name="cuffs"
            key={`${item.value}-${ind}`}
            col={3}
            colSp={6}
          />
        ))}
      </div>

      <div className="mtm-item__options" id="mtm-cuffs__option">
        {list.map((item, ind) => {
          const isActive = customizes?.cuffs?.value === item.value;
          return (
            <div
              className={`options-item mtm-item__selects ${isActive ? ' active' : ''}`}
              data-cuffs={item.value}
              key={`cuffs-sel-${ind}`}
            >
              {Array.isArray(item.cuffs_type) && item.cuffs_type.length > 0 ? (
                <>
                  {item.cuffs_type.map((cuffsType, subInd) => (
                    <OptionComponent
                      cleric={cuffsType}
                      ind={subInd}
                      parent="cuffs"
                      name="cuffs_type"
                      key={`cuffs-type-${item.id}-${subInd}-${customizes?.cuffs?.id || 'reset'}`}
                    />
                  ))}
                  <p style={{ width:'100%', margin: '24px 16px 0' }}>Tennis：Convertible style... no buttons</p>
                </>
              ) : (
                <p className="no-option-message" style={{ color: '#999', fontSize: '12px' }}>
                  No selectable cuff types for this option.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MtmCuffs;
